import { useCallback, useRef, useState } from 'react';

type AsyncCallback = (...args: any[]) => void | Promise<void>;

/**
 * Runs an async callback immediately on click while ignoring duplicate invocations
 * until the in-flight request completes. Exposes local pending state for instant UI feedback.
 */
const useGuardedAsyncCallback = <T extends AsyncCallback>(
  callback: T
): [T, boolean] => {
  const inFlightRef = useRef(false);
  const [isPending, setIsPending] = useState(false);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const guardedCallback = useCallback((...args: Parameters<T>) => {
    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    setIsPending(true);

    void Promise.resolve(callbackRef.current(...args)).finally(() => {
      inFlightRef.current = false;
      setIsPending(false);
    });
  }, []) as T;

  return [guardedCallback, isPending];
};

export default useGuardedAsyncCallback;
